import { CardContent, CardHeader, Divider, Input, InputLabel } from '@mui/material';
import { CardTitle } from '../CardTitle';
import { AdvancedKrarkConfigurationKeys } from 'types';
import { useAppDispatch, useAppSelector } from 'store';
import { selectConfig, updateConfig } from '../../store/configSlice';
import { PerX } from './PerX/PerX';
import { useTranslation } from 'react-i18next';
import { Users, Copy, Coins, Fingerprint } from 'lucide-react';

const ConfigPanel: React.FC = () => {
  const { t } = useTranslation();
  const krarkConfiguration = useAppSelector(selectConfig);
  const dispatch = useAppDispatch();

  return (
    <CardContent className="p-6">
      <CardHeader
        className="px-0 pt-0"
        title={
          <CardTitle className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Users className="w-6 h-6 text-indigo-600" />
            {t('configuration.title')}
          </CardTitle>
        }
      ></CardHeader>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="space-y-2">
          <InputLabel
            htmlFor="krarks"
            className="text-sm font-medium text-gray-700 flex items-center gap-2"
          >
            <Users className="w-4 h-4 text-indigo-600" />
            {t('configuration.krarks')}
          </InputLabel>
          <Input
            id="krarks"
            type="number"
            inputProps={{ min: 0 }}
            value={krarkConfiguration.krarksInPlay}
            onChange={(e) => dispatch(updateConfig({ krarksInPlay: Number(e.target.value) }))}
            className="h-12 text-lg border-2 border-indigo-200 focus:border-indigo-500 transition-all duration-200 hover:shadow-md"
          />
        </div>

        <div className="space-y-2">
          <InputLabel
            htmlFor="prodigies"
            className="text-sm font-medium text-gray-700 flex items-center gap-2"
          >
            <Fingerprint className="w-4 h-4 text-purple-600" />
            {t('configuration.harmonicProdigies')}
          </InputLabel>
          <Input
            id="prodigies"
            type="number"
            inputProps={{ min: 0 }}
            value={krarkConfiguration.harmonicProdigies}
            onChange={(e) => dispatch(updateConfig({ harmonicProdigies: Number(e.target.value) }))}
            className="h-12 text-lg border-2 border-purple-200 focus:border-purple-500 transition-all duration-200 hover:shadow-md"
          />
        </div>

        <div className="space-y-2">
          <InputLabel
            htmlFor="copies"
            className="text-sm font-medium text-gray-700 flex items-center gap-2"
          >
            <Copy className="w-4 h-4 text-pink-600" />
            {t('configuration.freeCopies')}
          </InputLabel>
          <Input
            id="copies"
            type="number"
            inputProps={{ min: 0 }}
            value={krarkConfiguration.freeCopies}
            onChange={(e) => dispatch(updateConfig({ freeCopies: Number(e.target.value) }))}
            className="h-12 text-lg border-2 border-pink-200 focus:border-pink-500 transition-all duration-200 hover:shadow-md"
          />
        </div>

        <div className="space-y-2">
          <InputLabel
            htmlFor="thumbs"
            className="text-sm font-medium text-gray-700 flex items-center gap-2"
          >
            <Coins className="w-4 h-4 text-amber-600" />
            {t('configuration.krarksThumbs')}
          </InputLabel>
          <Input
            id="thumbs"
            type="number"
            inputProps={{ min: 0 }}
            value={krarkConfiguration.krarksThumbs}
            onChange={(e) => dispatch(updateConfig({ krarksThumbs: Number(e.target.value) }))}
            className="h-12 text-lg border-2 border-amber-200 focus:border-amber-500 transition-all duration-200 hover:shadow-md"
          />
        </div>
      </div>

      <Divider className="my-8" />

      <div className="space-y-8">
        <h3 className="text-xl font-bold text-gray-900">{t('configuration.otherRewards')}</h3>

        {AdvancedKrarkConfigurationKeys.map((category) => {
          return (
            <PerX
              key={category}
              category={category}
              krarkPerXConfig={krarkConfiguration.advanced[category]}
            />
          );
        })}
      </div>
    </CardContent>
  );
};

export default ConfigPanel;
